import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function EmailPage() {
  const { isAdmin } = useAuth();
  const [caselle,     setCaselle]     = useState([]);
  const [selCasella,  setSelCasella]  = useState(null);
  const [cartelle,    setCartelle]    = useState([]);
  const [selCartella, setSelCartella] = useState('INBOX');
  const [messaggi,    setMessaggi]    = useState([]);
  const [selMsg,      setSelMsg]      = useState(null);
  const [msgDetail,   setMsgDetail]   = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [pagina,      setPagina]      = useState(1);
  const [total,       setTotal]       = useState(0);
  const [showNuova,   setNuova]       = useState(false);
  const [showSetup,   setSetup]       = useState(false);
  const [errore,      setErrore]      = useState('');
  const [searchQ,     setSearchQ]     = useState('');
  const [searchMode,  setSearchMode]  = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  // Carica caselle
  useEffect(() => {
    api.get('/email/caselle').then(r => {
      const list = r.data?.data || [];
      setCaselle(list);
      if (list.length > 0) setSelCasella(list[0]);
    }).catch(() => {});
  }, []);

  // Carica cartelle quando cambia casella
  useEffect(() => {
    if (!selCasella) return;
    api.get(`/email/cartelle?casellaId=${selCasella.id}`).then(r => {
      setCartelle(r.data?.data || []);
    }).catch(() => setCartelle([{ nome:'INBOX', fullName:'INBOX' }]));
  }, [selCasella]);

  // Carica messaggi
  const caricaMessaggi = useCallback(async () => {
    if (!selCasella || searchMode) return;
    setLoading(true); setErrore('');
    try {
      const r = await api.get(`/email/messaggi?casellaId=${selCasella.id}&cartella=${encodeURIComponent(selCartella)}&pagina=${pagina}`);
      setMessaggi(r.data?.data || []);
      setTotal(r.data?.total || 0);
    } catch(e) {
      setErrore(e.response?.data?.error || 'Errore caricamento email');
    } finally { setLoading(false); }
  }, [selCasella, selCartella, pagina, searchMode]);

  useEffect(() => { caricaMessaggi(); }, [caricaMessaggi]);

  const cercaEmail = async () => {
    if (!selCasella || !searchQ.trim()) return;
    setSearchLoading(true); setErrore(''); setSearchMode(true);
    setSelMsg(null); setMsgDetail(null);
    try {
      const r = await api.get(`/email/cerca?casellaId=${selCasella.id}&cartella=${encodeURIComponent(selCartella)}&q=${encodeURIComponent(searchQ.trim())}`);
      setMessaggi(r.data?.data || []);
      setTotal(r.data?.total || 0);
    } catch(e) {
      setErrore(e.response?.data?.error || 'Errore ricerca');
      setSearchMode(false);
    } finally { setSearchLoading(false); }
  };

  const apriMessaggio = async (msg) => {
    setSelMsg(msg);
    setMsgDetail(null);
    try {
      const r = await api.get(`/email/messaggio?casellaId=${selCasella.id}&uid=${msg.uid}&cartella=${encodeURIComponent(selCartella)}`);
      setMsgDetail(r.data?.data);
      // Segna come letto nella lista locale
      setMessaggi(list => list.map(m => m.uid === msg.uid ? {...m, letto: true} : m));
    } catch(e) { setMsgDetail({ error: e.response?.data?.error || 'Errore' }); }
  };

  const nonLetti = messaggi.filter(m => !m.letto).length;

  return (
    <div style={{ display:'grid', gridTemplateColumns:'220px 1fr', height:'calc(100vh - 60px)', overflow:'hidden' }}>

      {/* SIDEBAR SINISTRA */}
      <div style={{ borderRight:'1px solid #e5e7eb', display:'flex', flexDirection:'column', overflow:'hidden' }}>

        {/* Selezione casella */}
        <div style={{ padding:'10px 12px', borderBottom:'1px solid #e5e7eb', background:'#f9fafb' }}>
          <div style={{ fontSize:11, color:'#9ca3af', marginBottom:4, fontWeight:600 }}>CASELLA</div>
          <select className="form-control" style={{ fontSize:12, padding:'4px 6px' }}
            value={selCasella?.id || ''}
            onChange={e => {
              const c = caselle.find(x => x.id === parseInt(e.target.value));
              setSelCasella(c); setSelMsg(null); setMsgDetail(null); setPagina(1);
            }}>
            {caselle.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
          {caselle.length === 0 && (
            <div style={{ fontSize:11, color:'#9ca3af', marginTop:4 }}>
              Nessuna casella configurata
            </div>
          )}
        </div>

        {/* Bottoni azione */}
        <div style={{ padding:'8px 12px', borderBottom:'1px solid #e5e7eb', display:'flex', gap:6 }}>
          <button className="btn btn-primary btn-sm" style={{ flex:1, fontSize:11 }}
            onClick={() => { setNuova(true); setSelMsg(null); setMsgDetail(null); }}>
            ✏️ Nuova
          </button>
          <button className="btn btn-secondary btn-sm" style={{ fontSize:11 }} onClick={caricaMessaggi} title="Aggiorna">
            🔄
          </button>
          {isAdmin && (
            <button className="btn btn-secondary btn-sm" style={{ fontSize:11 }} onClick={() => setSetup(true)} title="Impostazioni">
              ⚙️
            </button>
          )}
        </div>

        {/* Ricerca */}
        <div style={{ padding:'8px 12px', borderBottom:'1px solid #e5e7eb' }}>
          <div style={{ display:'flex', gap:6 }}>
            <input
              className="form-control"
              style={{ fontSize:12, padding:'4px 8px', flex:1 }}
              placeholder="🔍 Cerca nelle email..."
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && cercaEmail()}
            />
            <button className="btn btn-secondary btn-sm" style={{ fontSize:11, whiteSpace:'nowrap' }}
              onClick={cercaEmail} disabled={searchLoading}>
              {searchLoading ? '...' : '🔍'}
            </button>
            {searchMode && (
              <button className="btn btn-secondary btn-sm" style={{ fontSize:11 }}
                onClick={() => { setSearchMode(false); setSearchQ(''); caricaMessaggi(); }}
                title="Cancella ricerca">✕</button>
            )}
          </div>
          {searchMode && (
            <div style={{ fontSize:11, color:'#6b7280', marginTop:4 }}>
              {messaggi.length} risultati per "{searchQ}"
            </div>
          )}
        </div>

        {/* Cartelle */}
        <div style={{ padding:'6px 0', borderBottom:'1px solid #e5e7eb', overflowY:'auto', maxHeight:150 }}>
          {[
            { nome: '📥 Posta in arrivo' + (nonLetti > 0 ? ` (${nonLetti})` : ''), fullName: 'INBOX' },
            ...cartelle.map(c => ({
              fullName: c.fullName,
              nome: c.nome === 'Sent'   ? '📤 Posta inviata' :
                    c.nome === 'Drafts' ? '📝 Bozze' :
                    c.nome === 'Trash'  ? '🗑 Cestino' :
                    c.nome === 'SPAM'   ? '🚫 Spam' :
                    c.nome === 'Templates' ? '📋 Modelli' :
                    `📁 ${c.nome}`
            }))
          ].map(({ nome, fullName }) => (
            <div key={fullName}
              onClick={() => { setSelCartella(fullName); setPagina(1); setSelMsg(null); setMsgDetail(null); }}
              style={{
                padding:'5px 16px', cursor:'pointer', fontSize:12,
                background: selCartella === fullName ? '#fff7ed' : 'transparent',
                color: selCartella === fullName ? '#b30000' : '#374151',
                fontWeight: selCartella === fullName ? 600 : 400,
                borderLeft: selCartella === fullName ? '3px solid #b30000' : '3px solid transparent',
              }}>
              {nome}
            </div>
          ))}
        </div>
      </div>

      {/* AREA PRINCIPALE */}
      <div style={{ display:'grid', gridTemplateColumns: selMsg||showNuova ? '320px 1fr' : '1fr', overflow:'hidden' }}>

        {/* LISTA MESSAGGI */}
        <div style={{ borderRight:'1px solid #e5e7eb', overflowY:'auto', display:'flex', flexDirection:'column' }}>
          {errore && (
            <div style={{ padding:'12px', background:'#fff5f5', color:'#dc2626', fontSize:12, borderBottom:'1px solid #fca5a5' }}>
              ⚠️ {errore}
            </div>
          )}
          {loading ? (
            <div style={{ padding:20, textAlign:'center' }}><div className="spinner"></div></div>
          ) : messaggi.length === 0 ? (
            <div style={{ padding:20, textAlign:'center', color:'#9ca3af', fontSize:13 }}>
              Nessun messaggio
            </div>
          ) : (
            messaggi.map(msg => (
              <div key={msg.uid}
                onClick={() => apriMessaggio(msg)}
                style={{
                  padding:'10px 12px', borderBottom:'1px solid #f3f4f6',
                  cursor:'pointer', background: selMsg?.uid === msg.uid ? '#fff7ed' : 'transparent',
                  borderLeft: selMsg?.uid === msg.uid ? '3px solid #b30000' : '3px solid transparent',
                }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:2 }}>
                  <span style={{ fontSize:12, fontWeight: msg.letto ? 400 : 700, color: msg.letto ? '#6b7280' : '#111827',
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>
                    {msg.from?.replace(/<.*>/,'').trim() || msg.from}
                  </span>
                  <span style={{ fontSize:10, color:'#9ca3af', whiteSpace:'nowrap', marginLeft:4 }}>{msg.date?.substring(0,10)}</span>
                </div>
                <div style={{ fontSize:12, fontWeight: msg.letto ? 400 : 600, color: msg.letto ? '#6b7280' : '#1f2937',
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {!msg.letto && <span style={{ display:'inline-block', width:6, height:6, borderRadius:'50%', background:'#b30000', marginRight:4, verticalAlign:'middle' }}></span>}
                  {msg.subject}
                </div>
              </div>
            ))
          )}
          {/* Paginazione */}
          {total > 20 && (
            <div style={{ padding:'8px 12px', display:'flex', gap:8, justifyContent:'center', borderTop:'1px solid #e5e7eb' }}>
              <button className="btn btn-secondary btn-sm" disabled={pagina===1} onClick={()=>setPagina(p=>p-1)}>‹</button>
              <span style={{ fontSize:12, color:'#6b7280', lineHeight:'28px' }}>Pag. {pagina}</span>
              <button className="btn btn-secondary btn-sm" disabled={pagina*20>=total} onClick={()=>setPagina(p=>p+1)}>›</button>
            </div>
          )}
        </div>

        {/* DETTAGLIO / NUOVA EMAIL */}
        {showNuova ? (
          <NuovaEmail caselle={caselle} selCasella={selCasella}
            onClose={() => setNuova(false)}
            onSent={() => { setNuova(false); caricaMessaggi(); }} />
        ) : selMsg && msgDetail ? (
          <DettaglioEmail msg={msgDetail} caselle={caselle} selCasella={selCasella} selCartella={selCartella}
            onRispondi={(m) => { setNuova(false); /* handled inline */ }}
            onClose={() => { setSelMsg(null); setMsgDetail(null); }}
            onRefresh={caricaMessaggi} />
        ) : (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', color:'#9ca3af', fontSize:13 }}>
            Seleziona un messaggio
          </div>
        )}
      </div>

      {/* MODAL SETUP CASELLE */}
      {showSetup && isAdmin && (
        <SetupCaselle caselle={caselle}
          onClose={() => setSetup(false)}
          onSaved={() => {
            api.get('/email/caselle').then(r => setCaselle(r.data?.data||[]));
            setSetup(false);
          }} />
      )}
    </div>
  );
}

// ── DETTAGLIO EMAIL ───────────────────────────────────────────
function DettaglioEmail({ msg, caselle, selCasella, selCartella, onClose, onRefresh }) {
  const [showRispondi, setRispondi] = useState(false);
  const [bodyRisposta, setBody]     = useState('');
  const [invio,        setInvio]    = useState(false);
  const [msgInvio,     setMsgInvio] = useState('');
  const [modBozza,     setModBozza] = useState(false);
  const [bozzaA,       setBozzaA]   = useState('');
  const [bozzaOgg,     setBozzaOgg] = useState('');
  const [bozzaTesto,   setBozzaTesto] = useState('');

  if (msg.error) return (
    <div style={{ padding:20, color:'#dc2626' }}>⚠️ {msg.error}</div>
  );


  const eBozza = (selCartella || '').toLowerCase().includes('draft')


              || (selCartella || '').toLowerCase().includes('bozz');



  const eliminaMessaggio = async () => {



    if (!window.confirm('Spostare questo messaggio nel cestino?')) return;



    setInvio(true);



    try {



      await api.post('/email/elimina', { casellaId: selCasella.id, uid: msg.uid, cartella: selCartella });



      setMsgInvio('🗑 Messaggio eliminato');



      setTimeout(() => { onRefresh && onRefresh(); onClose && onClose(); }, 900);



    } catch(e) { setMsgInvio('❌ ' + (e.response?.data?.error || e.message)); }



    finally { setInvio(false); }



  };




  const inviaBozza = async (conModifiche) => {


    if (!window.confirm('Inviare questa email?')) return;


    setInvio(true); setMsgInvio('');


    try {


      const corpo = { casellaId: selCasella.id, uid: msg.uid, cartella: selCartella };


      if (conModifiche) { corpo.destinatario = bozzaA; corpo.oggetto = bozzaOgg; corpo.testo = bozzaTesto; }


      const r = await api.post('/email/invia-bozza', corpo);


      setMsgInvio('✅ Inviata a ' + (r.data?.destinatari || '') +


                  (r.data?.allegati ? ' con ' + r.data.allegati + ' allegati' : ''));


      setTimeout(() => { onRefresh && onRefresh(); onClose && onClose(); }, 1500);


    } catch(e) { setMsgInvio('❌ ' + (e.response?.data?.error || e.message)); }


    finally { setInvio(false); }


  };



  const apriModifica = () => {


    setBozzaA(msg.to || msg.a || '');


    setBozzaOgg(msg.oggetto || msg.subject || '');


    setBozzaTesto(msg.testo || msg.body || msg.corpo || '');


    setModBozza(true);


  };



  const rispondi = async () => {
    setInvio(true); setMsgInvio('');
    try {
      const fromEmail = msg.from?.match(/<(.+)>/)?.[1] || msg.from;
      await api.post('/email/invia', {
        casellaId: selCasella.id,
        to:        fromEmail,
        subject:   `Re: ${msg.subject}`,
        body:      bodyRisposta + `\n\n--- Messaggio originale ---\n${msg.bodyText||''}`,
      });
      setMsgInvio('✅ Risposta inviata!');
      setBody('');
      setTimeout(() => { setRispondi(false); setMsgInvio(''); }, 2000);
    } catch(e) { setMsgInvio('Errore: ' + (e.response?.data?.error||e.message)); }
    finally { setInvio(false); }
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', overflow:'hidden' }}>
      {/* Header */}
      <div style={{ padding:'12px 16px', borderBottom:'1px solid #e5e7eb', background:'#f9fafb' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
          <h3 style={{ margin:0, fontSize:15, fontWeight:700, flex:1 }}>{msg.subject}</h3>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{ fontSize:12, color:'#6b7280', display:'grid', gap:2 }}>
          <div><strong>Da:</strong> {msg.from}</div>
          <div><strong>A:</strong> {msg.to}</div>
          {msg.cc && <div><strong>CC:</strong> {msg.cc}</div>}
          <div><strong>Data:</strong> {msg.date}</div>
        </div>
        <div style={{ marginTop:8, display:'flex', gap:6 }}>
          {eBozza && (
            <>
              <button className="btn btn-primary btn-sm" onClick={()=>inviaBozza(false)} disabled={invio}>
                {invio ? '...' : '📤 Invia'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={apriModifica}>✏️ Modifica</button>
            </>
          )}
          <button className="btn btn-sm" onClick={eliminaMessaggio} disabled={invio}
            style={{background:'#fee2e2', color:'#dc2626', border:'none'}}>🗑 Elimina</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setRispondi(v=>!v)}>
            {showRispondi ? '✕ Chiudi' : '↩️ Rispondi'}
          </button>
        </div>
      </div>

      {/* Form risposta */}

      {modBozza && (

        <div style={{ padding:12, background:'#f8fafc', borderRadius:8, marginTop:10 }}>

          <label className="form-label">Destinatario</label>

          <input className="form-control" value={bozzaA} onChange={e=>setBozzaA(e.target.value)} />

          <label className="form-label" style={{marginTop:8}}>Oggetto</label>

          <input className="form-control" value={bozzaOgg} onChange={e=>setBozzaOgg(e.target.value)} />

          <label className="form-label" style={{marginTop:8}}>Testo</label>

          <textarea className="form-control" rows={10} value={bozzaTesto} onChange={e=>setBozzaTesto(e.target.value)} />

          <div style={{ fontSize:11, color:'#6b7280', margin:'8px 0' }}>Gli allegati restano invariati.</div>

          <div style={{ display:'flex', gap:8 }}>

            <button className="btn btn-secondary btn-sm" onClick={()=>setModBozza(false)}>Annulla</button>

            <button className="btn btn-primary btn-sm" onClick={()=>inviaBozza(true)} disabled={invio}>

              {invio ? '...' : '📤 Invia'}</button>

          </div>

        </div>

      )}


      {showRispondi && (
        <div style={{ padding:'10px 16px', borderBottom:'1px solid #e5e7eb', background:'#fff7ed' }}>
          <textarea className="form-control" rows={4} value={bodyRisposta}
            onChange={e=>setBody(e.target.value)}
            placeholder="Scrivi la risposta..." style={{ fontSize:13, marginBottom:8 }} />
          {msgInvio && <div style={{ fontSize:12, color: msgInvio.includes('✅')?'#16a34a':'#dc2626', marginBottom:6 }}>{msgInvio}</div>}
          <button className="btn btn-primary btn-sm" onClick={rispondi} disabled={invio||!bodyRisposta.trim()}>
            {invio ? '...' : '📤 Invia risposta'}
          </button>
        </div>
      )}

      {/* Allegati */}
      {msg.allegati && msg.allegati.length > 0 && (
        <div style={{ padding:'8px 16px', borderBottom:'1px solid #e5e7eb', background:'#f9fafb' }}>
          <div style={{ fontSize:11, color:'#6b7280', marginBottom:6, fontWeight:600 }}>
            📎 {msg.allegati.length} allegat{msg.allegati.length===1?'o':'i'}
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {msg.allegati.map((a, i) => {
              const isImg = a.mimeType?.startsWith('image/');
              const isPdf = a.mimeType === 'application/pdf';
              const icon = isPdf ? '📄' : isImg ? '🖼' : '📎';
              const sizeKb = a.size ? Math.round(a.size/1024) : null;
              const downloadUrl = `data:${a.mimeType};base64,${a.b64}`;
              return (
                <a key={i} href={downloadUrl} download={a.nome}
                  style={{
                    display:'flex', alignItems:'center', gap:6,
                    padding:'5px 10px', borderRadius:6, fontSize:12,
                    background:'#fff', border:'1px solid #e5e7eb',
                    color:'#374151', textDecoration:'none', cursor:'pointer',
                  }}>
                  <span>{icon}</span>
                  <span style={{maxWidth:150, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{a.nome}</span>
                  {sizeKb && <span style={{color:'#9ca3af', fontSize:10}}>{sizeKb}KB</span>}
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Corpo email */}
      <div style={{ flex:1, overflowY:'auto', padding:'16px' }}>
        {msg.hasHtml ? (
          <iframe
            srcDoc={msg.bodyHtml}
            style={{ width:'100%', height:'100%', border:'none', minHeight:400 }}
            sandbox="allow-same-origin"
            title="email-body"
          />
        ) : (
          <pre style={{ fontSize:13, color:'#374151', whiteSpace:'pre-wrap', fontFamily:'inherit', margin:0 }}>
            {msg.bodyText || '(nessun contenuto)'}
          </pre>
        )}
      </div>
    </div>
  );
}

// ── NUOVA EMAIL ───────────────────────────────────────────────
function NuovaEmail({ caselle, selCasella, onClose, onSent }) {
  const [form, setForm] = useState({
    casellaId: selCasella?.id || caselle[0]?.id || '',
    to: '', cc: '', subject: '', body: '',
  });
  const [invio, setInvio] = useState(false);
  const [msg,   setMsg]   = useState('');

  const [allegati,  setAllegati]  = useState([]);
  const [upLoading, setUpLoading] = useState(false);

  const fmtSize = (b) => b >= 1048576 ? (b/1048576).toFixed(1)+' MB' : Math.max(1, Math.round(b/1024))+' KB';

  const onFile = async (e) => {
    const input = e.target;
    const files = Array.from(input.files || []);
    if (!files.length) return;
    setUpLoading(true); setMsg('');
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        const r = await api.post('/email/allegato', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setAllegati(a => [...a, { file: r.data.file, nome: r.data.nome, size: r.data.size }]);
      } catch(err) {
        setMsg(`Errore su ${f.name}: ` + (err.response?.data?.error || err.message));
      }
    }
    setUpLoading(false);
    input.value = '';
  };

  const rimuoviAllegato = (nomeFile) => setAllegati(a => a.filter(x => x.file !== nomeFile));


  const invia = async () => {
    if (!form.to.trim()) return setMsg('Inserisci il destinatario.');
    if (upLoading) return setMsg('Attendi il caricamento degli allegati.');
    setInvio(true); setMsg('');
    try {
      await api.post('/email/invia', { ...form, allegati });
      setMsg('✅ Email inviata!');
      setTimeout(onSent, 1500);
    } catch(e) { setMsg('Errore: ' + (e.response?.data?.error||e.message)); }
    finally { setInvio(false); }
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', padding:'16px', gap:10 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <h3 style={{ margin:0, fontSize:15 }}>✏️ Nuovo messaggio</h3>
        <button className="btn btn-secondary btn-sm" onClick={onClose}>✕</button>
      </div>
      <div className="form-group" style={{ margin:0 }}>
        <label className="form-label">Da</label>
        <select className="form-control" value={form.casellaId}
          onChange={e=>setForm(f=>({...f,casellaId:parseInt(e.target.value)}))}>
          {caselle.map(c=><option key={c.id} value={c.id}>{c.nome} ({c.email})</option>)}
        </select>
      </div>
      <div className="form-group" style={{ margin:0 }}>
        <label className="form-label">A *</label>
        <input className="form-control" value={form.to} onChange={e=>setForm(f=>({...f,to:e.target.value}))} placeholder="email@esempio.it" />
      </div>
      <div className="form-group" style={{ margin:0 }}>
        <label className="form-label">CC</label>
        <input className="form-control" value={form.cc} onChange={e=>setForm(f=>({...f,cc:e.target.value}))} placeholder="opzionale" />
      </div>
      <div className="form-group" style={{ margin:0 }}>
        <label className="form-label">Oggetto</label>
        <input className="form-control" value={form.subject} onChange={e=>setForm(f=>({...f,subject:e.target.value}))} />
      </div>
      <div className="form-group" style={{ margin:0, flex:1 }}>
        <label className="form-label">Messaggio</label>
        <textarea className="form-control" rows={8} value={form.body}
          onChange={e=>setForm(f=>({...f,body:e.target.value}))}
          style={{ resize:'vertical', height:'100%', minHeight:150 }} />
      </div>
      <div className="form-group" style={{ margin:0 }}>
        <label className="form-label">Allegati</label>
        <input type="file" multiple onChange={onFile} disabled={upLoading} style={{ fontSize:12 }} />
        {upLoading && <div style={{ fontSize:11, color:'#9ca3af', marginTop:4 }}>Caricamento…</div>}
        {allegati.length > 0 && (
          <div style={{ marginTop:6, display:'flex', flexDirection:'column', gap:4 }}>
            {allegati.map(a => (
              <div key={a.file} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12,
                background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:4, padding:'4px 8px' }}>
                <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>📎 {a.nome}</span>
                <span style={{ color:'#9ca3af', fontSize:11 }}>{fmtSize(a.size)}</span>
                <button className="btn btn-secondary btn-sm" style={{ fontSize:11, padding:'0 6px' }}
                  onClick={()=>rimuoviAllegato(a.file)} title="Rimuovi">✕</button>
              </div>
            ))}
          </div>
        )}
      </div>
      {msg && <div style={{ fontSize:12, color: msg.includes('✅')?'#16a34a':'#dc2626' }}>{msg}</div>}
      <button className="btn btn-primary" onClick={invia} disabled={invio || upLoading}>
        {invio ? '...' : '📤 Invia'}
      </button>
    </div>
  );
}

// ── SETUP CASELLE ─────────────────────────────────────────────
function SetupCaselle({ caselle, onClose, onSaved }) {
  const [form, setForm] = useState({
    nome:'', email:'', password:'',
    imapHost:'imaps.aruba.it', imapPort:993,
    smtpHost:'smtps.aruba.it', smtpPort:465,
  });
  const [saving, setSaving] = useState(false);
  const [msg,    setMsg]    = useState('');

  const aggiungi = async () => {
    setSaving(true); setMsg('');
    try {
      await api.post('/email/caselle', form);
      setMsg('✅ Casella aggiunta!');
      setTimeout(onSaved, 1000);
    } catch(e) { setMsg('Errore: ' + (e.response?.data?.error||e.message)); }
    finally { setSaving(false); }
  };

  const elimina = async (id) => {
    if (!window.confirm('Eliminare questa casella?')) return;
    await api.delete(`/email/caselle/${id}`);
    onSaved();
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div className="card" style={{ width:500, maxHeight:'80vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
          <h3 style={{ margin:0 }}>⚙️ Caselle email</h3>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>✕</button>
        </div>

        {/* Caselle esistenti */}
        {caselle.length > 0 && (
          <div style={{ marginBottom:16 }}>
            {caselle.map(c => (
              <div key={c.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid #e5e7eb' }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:13 }}>{c.nome}</div>
                  <div style={{ fontSize:12, color:'#6b7280' }}>{c.email}</div>
                </div>
                <button className="btn btn-danger btn-sm" onClick={() => elimina(c.id)}>🗑</button>
              </div>
            ))}
          </div>
        )}

        {/* Form nuova casella */}
        <h4 style={{ margin:'0 0 10px', fontSize:13, color:'#6b7280' }}>Aggiungi casella</h4>
        <div style={{ display:'grid', gap:8 }}>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Nome visualizzato</label>
            <input className="form-control" value={form.nome} onChange={e=>setForm(f=>({...f,nome:e.target.value}))} placeholder="Amministrazione" />
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Email</label>
            <input className="form-control" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="amministrazione@sosfire.it" />
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Password</label>
            <input type="password" className="form-control" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 80px 1fr 80px', gap:6 }}>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Server IMAP</label>
              <input className="form-control" value={form.imapHost} onChange={e=>setForm(f=>({...f,imapHost:e.target.value}))} />
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Porta</label>
              <input type="number" className="form-control" value={form.imapPort} onChange={e=>setForm(f=>({...f,imapPort:parseInt(e.target.value)}))} />
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Server SMTP</label>
              <input className="form-control" value={form.smtpHost} onChange={e=>setForm(f=>({...f,smtpHost:e.target.value}))} />
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Porta</label>
              <input type="number" className="form-control" value={form.smtpPort} onChange={e=>setForm(f=>({...f,smtpPort:parseInt(e.target.value)}))} />
            </div>
          </div>
        </div>
        {msg && <div style={{ fontSize:12, color: msg.includes('✅')?'#16a34a':'#dc2626', marginTop:8 }}>{msg}</div>}
        <button className="btn btn-primary" style={{ marginTop:12 }} onClick={aggiungi} disabled={saving}>
          {saving ? '...' : '➕ Aggiungi casella'}
        </button>
      </div>
    </div>
  );
}
